'use client';

import { useEffect, useState } from 'react';
import { ArrowUp, MessageCircle } from 'lucide-react';
import { requestSupport } from '@/lib/support/open-request';

type LivePricing = {
  totalLabel: string;
  subLabel: string;
};

/**
 * Sticky bestelbalk voor mobiel: op een lange productpagina (opties, staffels,
 * SEO-content) raakt de bezoeker de prijs en de bestelknop kwijt zodra hij
 * scrolt. De balk springt terug naar de configurator.
 *
 * Zodra de configurator een prijs heeft berekend zendt die een
 * `printen:pricing`-event uit; vanaf dat moment toont deze balk de werkelijke
 * totaalprijs van de huidige samenstelling. Zonder prijs vraagt hij om samenstellen.
 */
export function MobileCtaBar({
  priceLabel,
  subLabel,
  buttonLabel,
  productId,
  locale = 'nl',
}: {
  priceLabel: string;
  subLabel: string;
  buttonLabel: string;
  productId?: string;
  locale?: string;
}) {
  const [visible, setVisible] = useState(false);
  const [live, setLive] = useState<LivePricing | null>(null);

  // Direct zichtbaar, behalve zolang de eigen bestelknop van de configurator in beeld is.
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    const inView = new Set<Element>();
    const watch = () => {
      observer?.disconnect();
      inView.clear();
      observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) inView.add(entry.target); else inView.delete(entry.target);
        }
        setVisible(inView.size === 0);
      });
      document.querySelectorAll('[data-step-cta]').forEach((element) => observer?.observe(element));
      setVisible(inView.size === 0);
    };
    watch();
    // De configurator wisselt van stap en daarmee van knop; volg die wissel.
    let watched: Element[] = Array.from(document.querySelectorAll('[data-step-cta]'));
    const mutation = new MutationObserver(() => {
      const current = Array.from(document.querySelectorAll('[data-step-cta]'));
      if (current.length === watched.length && current.every((element, index) => element === watched[index])) return;
      watched = current;
      watch();
    });
    const root = document.getElementById('configurator');
    if (root) mutation.observe(root, { childList: true, subtree: true });
    return () => { observer?.disconnect(); mutation.disconnect(); };
  }, []);

  useEffect(() => {
    const onPricing = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { productId?: string; ready?: boolean; totalLabel?: string | null; subLabel?: string | null }
        | undefined;
      if (productId && detail?.productId !== productId) return;

      // Pending/invalid: show configuration guidance, not the previous amount.
      if (!detail?.ready || !detail.totalLabel) {
        setLive(null);
        return;
      }

      setLive({
        totalLabel: String(detail.totalLabel),
        subLabel: String(detail.subLabel || ''),
      });
    };

    window.addEventListener('printen:pricing', onPricing as EventListener);
    return () => window.removeEventListener('printen:pricing', onPricing as EventListener);
  }, [productId]);

  if (!visible) return null;

  return (
    <div data-mobile-order-bar className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
      {/* Dock the closed launcher in this bar; only an intentionally opened chat overlays content. */}
      <style>{`@media (max-width: 1023px) { #printen-assistant[data-open="false"] { display: none; } #printen-assistant[data-open="true"] { bottom: calc(5.25rem + env(safe-area-inset-bottom)); } }`}</style>
      <div className="mx-auto flex max-w-xl items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-base font-bold text-gray-900">{live ? live.totalLabel : priceLabel}</p>
          <p className="truncate text-xs text-gray-500">{live && live.subLabel ? live.subLabel : subLabel}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            const cta = live ? document.querySelector('[data-step-cta]') : null;
            if (cta) cta.scrollIntoView({ behavior: 'smooth', block: 'center' });
            else document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="inline-flex min-h-11 max-w-[45%] shrink-0 items-center gap-1.5 rounded-full bg-rose-600 px-3 py-2.5 text-sm font-semibold text-white shadow-md shadow-rose-200 transition hover:bg-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-600 focus-visible:ring-offset-2"
        >
          <ArrowUp className="h-4 w-4" />
          {buttonLabel}
        </button>
        <button type="button" onClick={requestSupport}
          aria-label={locale === 'de' ? 'Hilfe öffnen' : locale === 'fr' ? 'Ouvrir l’aide' : locale === 'en' ? 'Open help' : 'Hulp openen'}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-600">
          <MessageCircle className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
